/* ============================================================
   SQUICHE OS — Sidebar interaction & app launcher
   squiche.com

   Apps are defined as blocks in the Shopify theme editor
   (sections/os9-desktop.liquid). This file reads data-* attrs
   from the pre-rendered HTML — no hardcoded app list here.

   data-app-type values:
     finder, browser, contact, collection,
     game_snake, game_sniper, game_minesweeper,
     paint, emulator, cart

   ── ROADMAP 2026-05-18 ──────────────────────────────────────
   P0 — DONE: Scroll snap-back exempts focused inputs so iOS
        keyboard can appear (was snapping to 0,0 on keyboard scroll).
   P0 — DONE: Keyboard guard (initKeyboardGuard) fullscreens
        active window after keyboard settles; restores on blur.
   P0 — DONE: focusin now has a 500ms fallback so onKeyboardOpen
        fires even when visualViewport.resize doesn't (iOS edge case).
   P0 — DONE: handleViewport now skips when any input is focused,
        preventing the white-gap-below-statusbar bug.
   P0 — DONE: Statusbar rides up with keyboard (initStatusbarKeyboardRide).
   P0 — DONE: interactive-widget=overlays-content in viewport meta
        prevents layout resize when keyboard opens (theme.liquid).
   ── 2026-05-19 keyboard audit fixes ────────────────────────
   P0 — DONE: keyboardOpen stuck-state recovery — visualViewport.resize
        now calls onKeyboardClose() when vvp.height/innerHeight > 0.85
        and keyboardOpen is true. Prevents permanent silent breakage
        when focusout debounce silently skips closing (BUG 1+3).
   P0 — DONE: 500ms fallback conditioned on vvp.height < innerHeight*0.85
        so it only fires when keyboard is actually open; prevents
        false-positive fullscreen on desktop (BUG 2).
   P0 — DONE: _pendingTimer tracked in initKeyboardGuard; clearTimeout
        before each new focusin so timers don't stack on rapid focus (BUG 8).
   P0 — DONE: onKeyboardOpen top now adds vvp.offsetTop so window
        aligns correctly under menubar when iOS URL bar is visible (BUG 6).
   P0 — DONE: visualViewport.resize handler debounced 150ms via
        openAfterDebounce() so onKeyboardOpen fires only after vvp.height
        settles — fixes window being sized against a mid-animation height.
        500ms focusin fallback routes through same debounced path.
        onKeyboardOpen has a 0.65 sanity guard as last-resort protection.
   P0 — DONE: touch-action changed to manipulation in squiche-os.css
        so keyboard-triggering taps pass through on older iOS (BUG 4).
   P0 — DONE: user-select:text added to .os9-win inputs/textareas in
        os9-windows.css, overriding the broad user-select:none (BUG 5).
   ── 2026-05-20 keyboard overhaul ────────────────────────────
   P0 — DONE: Phase 1 — All keyboard handler paths emit [KB]
        console.log timestamps for device-side reproduction.
   P0 — DONE: Phase 2 — Single coordinator. _kbWinEl captured
        on focusin (before debounce) so window reference is never
        lost. keyboardOpen set FIRST to block handleViewport.
   P0 — DONE: Phase 3 — Geometry decoupled to window manager.
        squiche-os.js calls OS9Windows.enterKeyboardMode(winEl,h)
        and OS9Windows.exitKeyboardMode(). _kbSavedStyle removed
        from this file — window manager owns save/restore.
   P0 — DONE (2026-05-20): handleViewport split into position and
        size updates. top/left ALWAYS tracks vvp.offsetTop so the
        root stays anchored when iOS shifts the visual viewport on
        keyboard open (was: menubar slid above visible screen).
        width/height still skipped when keyboard/input active.
   P0 — DONE (2026-05-20): onKeyboardClose schedules a 300ms
        deferred root sync that bypasses skipSize. Keyboard dismiss
        button keeps input focused so handleViewport never restores
        root height — deferred sync forces it after animation.
   P0 — DONE (2026-05-20): onKeyboardClose blurs active input and
        calls window.scrollTo(0,0) before the deferred sync. iOS
        scrolls the document to expose the focused input; scroll lock
        exempts focused inputs so scrollY stays non-zero after dismiss.
        The blur drops the exemption, then scrollTo resets scroll so
        the deferred sync reads offsetTop=0 and fully restores root.
   P1 — TODO: Portrait lock — add @media(orientation:landscape)
        overlay in squiche-os.css with body.flash-game-active
        exception so Flash games can stay landscape.
   P2 — TODO: handleViewport skips kbZoomed but still fires on
        orientation change — could briefly fight keyboard guard.
        Low urgency; only affects edge case of rotating device
        while an input is focused.
   P2 — RESOLVED: squiche-os.js now loads first (script order 2026-05-24),
        so onKeyboardOpen registers on visualViewport.resize before
        snapWindowsIntoView does. Keyboard detection sets kbZoomed=1
        first; snapWindowsIntoView then correctly skips those windows.
        Previous note about snapWindowsIntoView firing first is no longer accurate.
   P0 — DONE (2026-05-24): [data-body].scrollTop reset in onKeyboardOpen.
        iOS "scroll to focused element" fires before keyboard finishes opening,
        scrolling the window body upward and hiding top form fields above the
        menubar. Fixed by resetting scrollTop=0 immediately after enterKeyboardMode
        and again in the 150ms settle timeout before the visibility check.
   P0 — DONE (2026-05-24): visualViewport.scroll NOT added to handleViewport —
        it caused root.style.top to update on every URL-bar event during scroll,
        jittering all root content. The scrollTop=0 reset was the real fix.
        Menubar jitter addressed instead with transform:translateZ(0) in CSS
        (GPU compositing layer keeps it painted independently of scroll).
   ───────────────────────────────────────────────────────────

   ── KEYBOARD / MOBILE NOTES (read before editing) ──────────

   iOS Safari does not resize the page when the keyboard opens
   because layout/theme.liquid uses:
     interactive-widget=overlays-content
   in the viewport meta tag. The keyboard overlays the page
   rather than pushing it. We use the visualViewport API to
   detect the keyboard and react to it in three ways:

   1. STATUSBAR RIDES UP (initStatusbarKeyboardRide)
      .os9-statusbar is position:fixed;bottom:0 in CSS so it
      is decoupled from the flex layout. When the keyboard
      opens, JS calculates:
        offset = window.innerHeight - vvp.height - vvp.offsetTop
      This equals keyboard height + Safari's floating chrome
      bar combined. Setting bar.style.bottom to that value
      makes the footer ride flush with the keyboard top edge
      without touching anything else on the page.

   2. ACTIVE WINDOW FULLSCREENS (initKeyboardGuard)
      We do NOT act on focusin immediately. A _pendingOpen flag
      is set and we wait for the visualViewport resize event,
      which fires only after the keyboard has finished animating
      and the viewport height has settled. This prevents sizing
      the window against the wrong (pre-keyboard) height.
      On focusout, after a 100ms debounce (to allow tabbing
      between fields without flickering), the window is
      restored to its saved style exactly.

   3. APP LAUNCHES BLOCKED (launchApp guard)
      While keyboardOpen === true, tapping sidebar icons does
      nothing. This prevents accidental new windows mid-typing.

   ── DRAG NOTE ───────────────────────────────────────────────

   Drag handle is .os9-win__titlebar (os9-windows.js initDrag).
   Resize is the bottom-right grip [data-resize] only — the OS9
   size-box corner. The bottom nav bar body is purely visual
   chrome and does not trigger resize or drag.

   ============================================================ */

(function () {
  'use strict';

  /* ── KEYBOARD GUARD STATE ── */
  var keyboardOpen = false;
  var _kbWinEl     = null; // captured on focusin before debounce; passed to window manager

  /* ── KEYBOARD LOG HELPER ── */
  /* Gated: this fires on every focus/blur/resize during mobile keyboard
     handling and was logging unconditionally in production. Enable with
     ?kbdebug=1 when reproducing a keyboard issue on a device. */
  var _KB_DEBUG = (function () {
    try { return new URLSearchParams(window.location.search).has('kbdebug'); }
    catch (e) { return false; }
  })();
  function _kbLog(msg) { if (_KB_DEBUG) console.log('[KB +' + performance.now().toFixed(0) + 'ms]', msg); }

  /* ── LAUNCH APP — reads config from the icon element's data attrs ── */
  function launchApp(iconEl) {
    if (keyboardOpen) return; // block new app launches while keyboard is open

    const type  = iconEl.dataset.appType;
    const label = iconEl.querySelector('.os9-sidebar-icon__label')?.textContent?.trim() || 'App';

    switch (type) {
      case 'finder':
        if (window.OS9Finder) window.OS9Finder.open();
        break;

      case 'browser':
        if (window.OS9Browser) window.OS9Browser.open({ url: iconEl.dataset.pageUrl || null });
        break;

      case 'contact':
        if (window.OS9Windows && window.OS9Windows.openPage) window.OS9Windows.openPage({
          url: iconEl.dataset.pageUrl || '/pages/contact',
          title: label,
        });
        break;

      case 'collection':
        if (window.OS9Windows && window.OS9Windows.openCollection) window.OS9Windows.openCollection({
          handle: iconEl.dataset.collectionHandle,
          title: iconEl.dataset.collectionTitle || label,
        });
        break;

      case 'game_snake':
        if (window.OS9Apps) window.OS9Apps.openSnake();
        break;

      case 'game_sniper':
        if (window.OS9Apps) window.OS9Apps.openSniperAssassin();
        break;

      case 'game_minesweeper':
        if (window.OS9Apps) window.OS9Apps.openMinesweeper();
        break;

      case 'game_kitten_cannon':
        if (window.OS9Finder) window.OS9Finder.openGameById('game-kitten-cannon');
        break;

      case 'game_xiaoxiao':
        if (window.OS9Finder) window.OS9Finder.openGameById('game-xiaoxiao');
        break;

      case 'game_line_rider':
        if (window.OS9Finder) window.OS9Finder.openGameById('game-line-rider');
        break;

      case 'game_impossible_quiz':
        if (window.OS9Finder) window.OS9Finder.openGameById('game-impossible-quiz');
        break;

      case 'game_emulator':
        if (window.AppEmulator) window.AppEmulator.open();
        break;

      case 'game_budcalc':
        if (window.OS9Apps) window.OS9Apps.openBudCalc();
        break;

      case 'games_folder':
        if (window.OS9Finder) window.OS9Finder.openGames();
        break;

      case 'paint':
        if (window.AppJSPaint) {
          window.AppJSPaint.open();
        } else {
          var retry = setInterval(function () {
            if (window.AppJSPaint) { clearInterval(retry); window.AppJSPaint.open(); }
          }, 100);
          setTimeout(function () { clearInterval(retry); }, 3000);
        }
        break;

      case 'emulator':
        if (window.AppEmulator) window.AppEmulator.open();
        break;

      case 'cart':
        /* Trash is cosmetic — it opens the same Finder as Squiche HD.
           Only a genuine cart icon lands on the cart frame. */
        if (iconEl.classList.contains('is-trash')) {
          if (window.OS9Windows && window.OS9Windows.openFinder) window.OS9Windows.openFinder({});
        } else if (window.OS9Windows && window.OS9Windows.openCart) {
          window.OS9Windows.openCart({ title: label });
        }
        break;
    }
  }

  /* ── ICON INTERACTION ── */
  function initIconInteraction() {
    const dock = document.getElementById('os9-icon-dock');
    if (!dock) return;

    // Single click = select + launch (desktop)
    dock.addEventListener('click', function (e) {
      const icon = e.target.closest('.os9-sidebar-icon');
      if (!icon) return;
      dock.querySelectorAll('.os9-sidebar-icon').forEach(function (i) {
        i.classList.remove('is-selected');
      });
      icon.classList.add('is-selected');
      launchApp(icon);
    });

    // Single tap = launch (mobile)
    dock.addEventListener('touchend', function (e) {
      const icon = e.target.closest('.os9-sidebar-icon');
      if (!icon) return;
      e.preventDefault();
      launchApp(icon);
    });

    // Click outside dock = deselect all
    document.addEventListener('click', function (e) {
      if (!e.target.closest('#os9-icon-dock')) {
        dock.querySelectorAll('.os9-sidebar-icon').forEach(function (i) {
          i.classList.remove('is-selected');
        });
      }
    });
  }

  /* Remember the merchant's label the first time we touch it, so a count can
     be appended and removed without ever destroying what they typed in the
     theme editor. */
  function baseLabel(el) {
    if (!el.dataset.baseLabel) el.dataset.baseLabel = el.textContent.trim();
    return el.dataset.baseLabel;
  }

  /* ── UPDATE TRASH ICON ──
     Trash swaps between its empty and full artwork as the cart fills, but
     never shows a number — that is the Cart icon's job. Both images come
     from the block's own settings. */
  window.OS9TrashUpdate = function (count) {
    document.querySelectorAll('.os9-sidebar-trash').forEach(function (icon) {
      const label = icon.querySelector('.os9-sidebar-icon__label');
      const img   = icon.querySelector('.os9-sidebar-icon__img img');
      if (label) label.textContent = baseLabel(label);
      if (img) {
        const empty = icon.dataset.iconEmpty || '';
        const full  = icon.dataset.iconFull  || '';
        const next  = count > 0 ? (full || empty) : (empty || full);
        if (next && img.getAttribute('src') !== next) img.setAttribute('src', next);
      }
    });
  };

  /* ── UPDATE CART COUNT ──
     Driven by the block's "Show cart item count" setting, not by app type or
     block order, so reordering the sidebar can never move the count onto the
     wrong icon. */
  window.OS9CartUpdate = function (count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (icon) {
      const label = icon.querySelector('.os9-sidebar-icon__label');
      if (!label) return;
      const base = baseLabel(label);
      label.textContent = count > 0 ? base + ' (' + count + ')' : base;
    });
  };

  /* ── STATUSBAR KEYBOARD RIDE ────────────────────────────────
     The statusbar is position:fixed;bottom:0 in squiche-os.css,
     decoupled from the flex layout so the page never resizes.

     When the iOS keyboard opens, visualViewport shrinks but
     window.innerHeight stays the same. The gap between them
     equals the keyboard height + Safari's floating chrome bar.
     We push the statusbar up by exactly that amount so it sits
     flush against the keyboard top edge.

     offset = window.innerHeight - vvp.height - vvp.offsetTop

     On keyboard close, offset returns to 0 and the bar snaps
     back to the bottom of the screen.
  ── */
  function initStatusbarKeyboardRide() {
    if (!window.visualViewport) return;

    var statusbar = document.querySelector('.os9-statusbar');
    if (!statusbar) return;

    function updateBarPosition() {
      var vvp    = window.visualViewport;
      var offset = window.innerHeight - vvp.height - vvp.offsetTop;
      statusbar.style.bottom = Math.max(0, offset) + 'px';
    }

    window.visualViewport.addEventListener('resize', updateBarPosition);
    window.visualViewport.addEventListener('scroll', updateBarPosition);
    updateBarPosition();
  }

  /* ── KEYBOARD GUARD ─────────────────────────────────────────
     Fullscreens the active window after the keyboard is fully
     open, then restores it when the keyboard closes.
     See header notes for full explanation of the timing logic.
  ── */
  function getActiveWindow() {
    return document.querySelector('.os9-win.is-active') || null;
  }

  /* ── KEYBOARD COORDINATOR ───────────────────────────────────
     Called after the 150ms resize-settle debounce. Sets
     keyboardOpen = true FIRST to block handleViewport, then
     delegates geometry + style save to OS9Windows.enterKeyboardMode.
     _kbWinEl was captured on focusin before any debounce timer.
  ── */
  function onKeyboardOpen() {
    var vvp = window.visualViewport;
    _kbLog('[KB] onKeyboardOpen — running, activeWindow: ' + (_kbWinEl ? 'found' : 'null') + ', vvp.height: ' + (vvp ? vvp.height.toFixed(0) : 'n/a'));

    if (keyboardOpen) return;
    // Sanity: if vvp is still > 65% of innerHeight keyboard hasn't opened far enough.
    if (vvp && vvp.height > window.innerHeight * 0.65) {
      _kbLog('[KB] onKeyboardOpen — bailing (vvp.height > 65% of innerHeight)');
      return;
    }

    keyboardOpen = true; // block handleViewport BEFORE any geometry changes

    if (_kbWinEl && window.OS9Windows && window.OS9Windows.enterKeyboardMode) {
      var mb  = document.getElementById('os9-menubar');
      var sb  = document.querySelector('.os9-statusbar');
      var mbH = mb ? mb.offsetHeight : 22;
      var sbH = sb ? sb.offsetHeight : 24;
      var rH  = vvp ? vvp.height : window.innerHeight;
      // enterKeyboardMode saves style and applies fullscreen geometry (see os9-window-manager.js)
      window.OS9Windows.enterKeyboardMode(_kbWinEl, rH - mbH - sbH);
      // iOS "scroll focused element into view" fires before the keyboard finishes
      // opening — it scrolls [data-body] upward, hiding form fields above the
      // menubar. Reset immediately after enterKeyboardMode so the window starts
      // from the top; the fullscreened window is tall enough to show the element.
      var _immediateBody = _kbWinEl.querySelector('[data-body]');
      if (_immediateBody) _immediateBody.scrollTop = 0;
      // 150ms lets the window fullscreen layout settle before we measure.
      // Targets [data-body] directly so we scroll inside the window, not the document.
      setTimeout(function () {
        var ae = document.activeElement;
        if (!ae || (ae.tagName !== 'INPUT' && ae.tagName !== 'TEXTAREA')) return;
        var scrollEl = ae.closest('[data-body]');
        if (scrollEl) {
          // Reset again in case iOS re-scrolled during the keyboard animation.
          scrollEl.scrollTop = 0;
          var aeRect = ae.getBoundingClientRect();
          var scRect = scrollEl.getBoundingClientRect();
          // Only scroll if the element is genuinely clipped by the container bottom.
          if (aeRect.bottom > scRect.bottom - 16) {
            scrollEl.scrollTop = Math.max(0, aeRect.bottom - scRect.bottom + 24);
          }
        } else {
          ae.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 150);
    }
  }

  function onKeyboardClose() {
    _kbLog('[KB] onKeyboardClose — _kbWinEl: ' + (_kbWinEl ? 'found' : 'null') + ', delegating restore to OS9Windows.exitKeyboardMode');
    if (!keyboardOpen) return;
    keyboardOpen = false;
    // Style restore is owned by the window manager — it saved it in enterKeyboardMode
    if (window.OS9Windows && window.OS9Windows.exitKeyboardMode) {
      window.OS9Windows.exitKeyboardMode();
    }
    _kbWinEl = null;
    // Blur the focused input and reset document scroll.
    // iOS scrolled the document to bring the focused input into view; our scroll lock
    // exempts focused inputs so scroll.Y stays non-zero after keyboard dismiss.
    // Blurring first drops the exemption, then scrollTo(0,0) resets the document.
    var ae = document.activeElement;
    if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable)) {
      ae.blur();
    }
    window.scrollTo(0, 0);
    // Force root back to full size once the keyboard close animation settles.
    // handleViewport skips size updates while an input is focused (dismiss button
    // keeps the input focused), so the height gap never self-heals without this.
    if (window.visualViewport) {
      setTimeout(function () {
        if (keyboardOpen) return; // user re-opened keyboard before timer fired
        var vvp  = window.visualViewport;
        var root = document.querySelector('.os9-section-root');
        if (!root) return;
        root.style.top    = vvp.offsetTop  + 'px';
        root.style.left   = vvp.offsetLeft + 'px';
        root.style.width  = vvp.width  + 'px';
        root.style.height = vvp.height + 'px';
      }, 300);
    }
  }

  function initKeyboardGuard() {
    var _pendingOpen   = false;
    var _pendingTimer  = null; // 500ms focusin fallback timer
    var _debounceTimer = null; // 150ms resize-settle debounce timer

    // All paths to onKeyboardOpen route here. Resets on every
    // visualViewport.resize so coordinator fires only after vvp.height settles.
    function openAfterDebounce() {
      clearTimeout(_debounceTimer);
      _debounceTimer = setTimeout(function () {
        _pendingOpen = false;
        onKeyboardOpen();
      }, 150);
    }

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', function () {
        var vvp = window.visualViewport;
        _kbLog('[KB] visualViewport resize — vvp.height: ' + vvp.height.toFixed(0) + ', keyboardOpen: ' + keyboardOpen + ', _pendingOpen: ' + _pendingOpen);

        // Stuck-state recovery: if vvp returns to near full-height while keyboardOpen
        // is stuck true (focusout never fired), force close without waiting for focusout.
        if (keyboardOpen && !_pendingOpen && vvp.height / window.innerHeight > 0.85) {
          onKeyboardClose();
          return;
        }

        if (_pendingOpen) {
          openAfterDebounce();
        }
      });
    }

    document.addEventListener('focusin', function (e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        // Capture the active window IMMEDIATELY — before focus possibly moves.
        // The coordinator uses this reference; don't re-query at debounce settle time.
        _kbWinEl = getActiveWindow();
        _kbLog('[KB] focusin fired — target: ' + e.target.tagName + ', keyboardOpen: ' + keyboardOpen + ', _pendingOpen: ' + _pendingOpen);

        if (window.visualViewport) {
          _pendingOpen = true;
          clearTimeout(_pendingTimer);
          // 500ms fallback: if visualViewport.resize never fires, check vvp.height directly.
          // Routes through openAfterDebounce — never calls onKeyboardOpen directly.
          _pendingTimer = setTimeout(function () {
            if (_pendingOpen) {
              if (window.visualViewport.height < window.innerHeight * 0.85) openAfterDebounce();
              else _pendingOpen = false;
            }
          }, 500);
        } else {
          setTimeout(onKeyboardOpen, 400);
        }
      }
    });

    document.addEventListener('focusout', function (e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        _pendingOpen = false;
        // 100ms debounce so tabbing between fields doesn't briefly close+reopen
        setTimeout(function () {
          var ae = document.activeElement;
          _kbLog('[KB] focusout debounce resolved — activeElement: ' + (ae ? ae.tagName : 'none'));
          if (!ae || (ae.tagName !== 'INPUT' && ae.tagName !== 'TEXTAREA' && !ae.isContentEditable)) {
            onKeyboardClose();
          }
        }, 100);
      }
    });
  }

  /* ── AUTO-OPEN — launch apps flagged data-auto-open="true" at startup ── */
  function autoOpenApps() {
    const icons = document.querySelectorAll('#os9-icon-dock [data-auto-open="true"]');
    icons.forEach(function (icon, i) {
      setTimeout(function () {
        launchApp(icon);
        /* Restored in the SAME tick as the launch. Windows are built
           synchronously and focus themselves on creation, so correcting the
           stack here happens before the browser can paint. Doing this on a
           later timer — even a 100ms one — is what made Squiche HD visibly
           appear, get covered by the browser, then jump back to the front. */
        raiseStorefront();
      }, i * 300);
    });
    /* Safety net for any app type that builds asynchronously and so focuses
       itself after its launch tick has already ended. */
    if (icons.length) setTimeout(raiseStorefront, (icons.length - 1) * 300 + 400);
  }

  /* Squiche HD ends up in FRONT of whatever else opened itself at startup. It
     is the storefront — a visitor who lands facing the browser has to go
     looking for the shop — so it gets the top of the stack.

     Raised explicitly rather than by ordering the dock: launches are staggered
     and whichever window is built last wins the z-index, which is not something
     block order in the theme editor reliably controls. Lands well under the
     sticky note, which sets its own z to _os9TopZ + 50. */
  function raiseStorefront() {
    if (userHasInteracted) return;      /* never fight a deliberate click */
    var hd = document.getElementById('os9-cart-win');
    if (!hd || !window.OS9Windows || !window.OS9Windows.focusWindow) return;

    /* Only act when something is actually above it. focusWindow() bumps the
       z-counter on every call, so re-raising an already-front window would
       churn the stack for nothing. */
    var top = 0;
    document.querySelectorAll('.os9-win').forEach(function (w) {
      var z = parseInt(getComputedStyle(w).zIndex, 10) || 0;
      if (z > top) top = z;
    });
    if ((parseInt(getComputedStyle(hd).zIndex, 10) || 0) < top) {
      window.OS9Windows.focusWindow(hd);
    }
  }

  /* If the visitor has already picked a window themselves, the startup
     preference stops applying — their choice outranks ours. */
  var userHasInteracted = false;
  ['mousedown', 'touchstart', 'keydown'].forEach(function (evt) {
    document.addEventListener(evt, function () { userHasInteracted = true; },
      { once: true, passive: true, capture: true });
  });

  /* ── SCROLL LOCK — prevent any page-level scroll ── */
  function initScrollLock() {
    // Snap back instantly if anything moves the viewport — but NOT when an input is focused,
    // because iOS scrolls internally to bring the keyboard-focused input into view.
    window.addEventListener('scroll', function() {
      var ae = document.activeElement;
      if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable)) return;
      window.scrollTo(0, 0);
    }, { passive: true });

    // Block touchmove everywhere except window content areas, the Ruffle player,
    // and the resize grip — [data-resize] needs iOS to honour the drag gesture so
    // the size-box corner can resize the window. Title bar ([data-drag]) and bottom
    // nav bar body are NOT exempted: the title bar's own touchmove handler calls
    // preventDefault(), and the bar body is inert (no resize listener).
    document.addEventListener('touchmove', function(e) {
      // Block pinch-to-zoom (multi-touch) everywhere except the full-image detail wrapper.
      // Single-finger scroll in [id^="os9-page-"] still passes through unchanged.
      if (e.touches && e.touches.length > 1) {
        if (!e.target.closest('.geo-full-img-wrap')) e.preventDefault();
        return;
      }
      if (e.target.closest('ruffle-player, [data-resize], [id^="os9-page-"], .os9-browser-content')) return;
      e.preventDefault();
    }, { passive: false });

    // iOS Safari fires gesturestart then gesturechange during a pinch.
    // Prevent both everywhere except the full-image detail view.
    document.addEventListener('gesturestart', function(e) {
      if (!e.target.closest('.geo-full-img-wrap')) e.preventDefault();
    }, { passive: false });
    document.addEventListener('gesturechange', function(e) {
      if (!e.target.closest('.geo-full-img-wrap')) e.preventDefault();
    }, { passive: false });

    // ── Viewport zoom lock ───────────────────────────────────────────
    // If iOS has stored a zoomed state from a previous visit (or the
    // user managed to zoom before our gesture handlers fired), snap the
    // viewport back to scale=1 by briefly applying maximum-scale=1.
    // This also fires on every visualViewport resize so live zooms are caught.
    (function() {
      if (!window.visualViewport) return;
      function snapToScale1() {
        if (window.visualViewport.scale <= 1.01) return;
        var m = document.querySelector('meta[name="viewport"]');
        if (!m) return;
        var orig = m.getAttribute('content');
        m.setAttribute('content', orig + ',minimum-scale=1,maximum-scale=1');
        setTimeout(function() { m.setAttribute('content', orig); }, 400);
      }
      snapToScale1();                                   // on script eval
      window.addEventListener('load', snapToScale1);    // after full page load
      window.visualViewport.addEventListener('resize', function() {
        if (window.visualViewport.scale > 1.01) snapToScale1();
      });
    })();

    // JS-driven swipe scroll for [data-body] scroll containers.
    // Needed because .os9-section-root uses touch-action:none (to keep the custom webkit
    // OS9 scrollbars permanently visible). That blocks native iOS swipe-to-scroll, so we
    // replicate it manually by tracking touch delta and updating scrollTop directly.
    var _scrollEl = null;
    var _scrollY  = 0;
    document.addEventListener('touchstart', function(e) {
      if (e.target.closest('[id^="os9-page-"], .os9-browser-content')) { _scrollEl = null; _scrollY = 0; return; }
      var el = e.target.closest('[data-body]');
      _scrollEl = el || null;
      _scrollY  = el ? e.touches[0].clientY : 0;
    }, { passive: true });
    document.addEventListener('touchmove', function(e) {
      if (!_scrollEl) return;
      var dy = _scrollY - e.touches[0].clientY;
      _scrollEl.scrollTop += dy;
      _scrollY = e.touches[0].clientY;
    }, { passive: true });
    document.addEventListener('touchend', function(e) {
      _scrollEl = null;
      // iOS pre-15 keyboard fix: touch-action:none on the root can prevent the browser
      // from triggering the virtual keyboard on input tap. Explicitly focusing on touchend
      // ensures the keyboard appears even on older iOS versions.
      var t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
        if (document.activeElement !== t) t.focus();
      }
    }, { passive: true });
  }

  /* ── INIT ── */
  /* ── initPageZoomGuard ──────────────────────────────────────────
     Locks browser-level page zoom so a trackpad pinch on macOS or a
     two-finger pinch on iOS doesn't rescale the entire OS9 desktop
     (sidebar icons turn into giants, browser window shifts off-screen,
     etc.) The viewport <meta> and touch-action: manipulation already
     try to do this but BOTH are bypassed by:
       • macOS trackpad pinch — reported as wheel + ctrlKey, not a
         touch gesture, so touch-action is ignored
       • iOS Safari 13+ — ignores user-scalable=no for accessibility,
         fires its own non-standard 'gesturestart' / 'gesturechange' /
         'gestureend' events that need explicit preventDefault.

     SquichaVista's fullscreen image viewer (.sqv-fs) is a legitimate
     zoom zone — pinch there is supposed to zoom the image. We skip
     the guard when the event target is inside that overlay.
     ──────────────────────────────────────────────────────────────── */
  function initPageZoomGuard() {
    /* Block native page-zoom everywhere. SquichaVista's fullscreen viewer
       implements its own pinch-zoom that transforms only the <img>, so we
       no longer need to exempt .sqv-fs here — native pinch in fullscreen
       would zoom the entire viewport including our overlay chrome, which
       is not what the user wants. */
    /* Mac trackpad pinch + Ctrl+scroll page-zoom intent */
    window.addEventListener('wheel', function (e) {
      if (!e.ctrlKey) return;
      e.preventDefault();
    }, { passive: false });
    /* iOS gesture* events — non-standard but only way to block pinch
       on iOS Safari since it ignores user-scalable=no */
    ['gesturestart', 'gesturechange', 'gestureend'].forEach(function (name) {
      document.addEventListener(name, function (e) {
        e.preventDefault();
      }, { passive: false });
    });
    /* Belt-and-suspenders: if anything else manages to push document.body
       into a scaled state, reset on next tick. Won't fight zoom inside
       .sqv-fs because that overlay uses its own transform on the image,
       not document.body. */
    document.addEventListener('touchend', function () {
      if (window.visualViewport && window.visualViewport.scale > 1.01) {
        try {
          document.body.style.zoom = 1;
          if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
        } catch (e) {}
      }
    }, { passive: true });
  }

  function init() {
    initIconInteraction();
    initKeyboardGuard();
    initStatusbarKeyboardRide();
    initScrollLock();
    initPageZoomGuard();
    setTimeout(autoOpenApps, 600);

    // Keep the desktop viewport locked to the visual viewport on mobile.
    // POSITION (top/left) always tracks vvp.offsetTop/offsetLeft — even while a
    // keyboard is open — because iOS shifts the visual viewport when bringing an
    // input into view, and if we don't follow it the root slides above the screen
    // and the menubar disappears.
    // SIZE (width/height) is skipped while the keyboard is active or an input is
    // focused — shrinking the root height during keyboard open creates a white gap
    // below the statusbar. The keyboard guard owns window sizing in that state.
    if (window.visualViewport) {
      function handleViewport() {
        var vvp      = window.visualViewport;
        var ae       = document.activeElement;
        var skipSize = keyboardOpen || !!(ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable));
        _kbLog('[KB] handleViewport — running, keyboardOpen: ' + keyboardOpen + ', skipSize: ' + skipSize);
        const root = document.querySelector('.os9-section-root');
        if (!root) return;
        // Always update position — keeps root anchored to visual viewport
        root.style.top  = vvp.offsetTop  + 'px';
        root.style.left = vvp.offsetLeft + 'px';
        var mb  = document.getElementById('os9-menubar');
        var mbH = mb ? mb.offsetHeight : 22;
        // Keep mobileContact email window top in sync on every viewport change
        // (mirrors syncKeyboardModeTop but runs unconditionally, not just when keyboardOpen)
        if (window.OS9Windows && window.OS9Windows.syncMobileContactTop) {
          window.OS9Windows.syncMobileContactTop(vvp.offsetTop + mbH);
        }
        if (skipSize) {
          // Keep keyboard-mode window top in sync with the visual viewport.
          // vvp.offsetTop can shift after enterKeyboardMode fires (e.g. iOS autocomplete bar
          // appears after the keyboard settles), which would otherwise push the window above
          // the menubar (screenshot bug where titlebar appears above the menubar strip).
          if (keyboardOpen && window.OS9Windows && window.OS9Windows.syncKeyboardModeTop) {
            window.OS9Windows.syncKeyboardModeTop(vvp.offsetTop + mbH);
          }
          return;
        }
        root.style.width  = vvp.width  + 'px';
        root.style.height = vvp.height + 'px';
      }
      window.visualViewport.addEventListener('resize', handleViewport);
      window.visualViewport.addEventListener('scroll', handleViewport);
      window.addEventListener('orientationchange', function () {
        setTimeout(handleViewport, 100);
      });
      handleViewport();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();